import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar } from 'recharts';
import { AlertCircle, Settings, TrendingUp } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, getMetricLabel, saveChartConfig } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import MatomoChannelsChart from './MatomoChannelsChart';
import MatomoGeographyChart from './MatomoGeographyChart';
import RepartitionChart from './RepartitionChart';
import MatomoEcommerceProducts from './MatomoEcommerceProducts';
import MatomoEventsTable from './MatomoEventsTable';
// Les mêmes icônes que les composants Google Analytics équivalents.
import { Monitor, Globe, Languages, Calendar, FileText } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import { marqueursDeNotes, useNotesDeGraphique, InfobulleDeNotes } from './notes/marqueursDeNotes';

// Classification des métriques Matomo : VALEURS (pour histogramme) vs RATIOS (pour courbe)
// Les métriques ajoutées pour aligner les trois solutions de mesure doivent être déclarées
// **partout** : classification, correspondance de clés, configuration des barres et de la
// courbe, et lecture de la valeur. En oublier une fait retomber le graphique sur sa métrique
// par défaut — la carte se surligne, et la courbe montre autre chose.
const MATOMO_VALUE_METRICS = ['visites', 'pages_vues', 'conv', 'valeur_conv', 'transactions', 'chiffre_affaires'];
const MATOMO_RATIO_METRICS = ['taux_conv', 'rebond', 'duree_moy', 'pages_visite', 'taux_transaction', 'panier_moyen'];

// Mapping clé KPI → clé graphique
const MATOMO_METRIC_KEY_MAP = {
  visites: 'visits',
  pages_vues: 'pageviews',
  conv: 'conversions',
  valeur_conv: 'conversionsValue',
  taux_conv: 'conversionRate',
  rebond: 'bounceRate',
  duree_moy: 'avgTimeOnSite',
  pages_visite: 'actionsPerVisit',
  transactions: 'transactions',
  chiffre_affaires: 'totalRevenue',
  taux_transaction: 'transactionRate',
  panier_moyen: 'avgOrderValue'
};

/**
 * MatomoPerformanceChart - Composant de graphique pour les métriques Matomo Analytics
 * Style identique à Google Ads PerformanceChart
 *
 * Métriques affichées:
 * 1. Sessions (visites) - équivalent Clics
 * 2. Taux de Conversion
 * 3. Conversions
 * 4. Pages vues - équivalent Coût (mais sans €)
 * 5. % de Rebond - équivalent CPC
 * 6. Durée moyenne - équivalent CPA
 * 7. Visiteurs uniques - équivalent Valeur Conv
 * 8. Pages/Visite - équivalent COS
 */

const MatomoPerformanceChart = ({
  data,
  /**
   * Notes à reporter sur le graphique.
   *
   * Le mécanisme n'existait que sur les dashboards de régies : une note cochée « afficher
   * sur les graphiques » et rattachée à une propriété analytique ne s'affichait nulle part.
   */
  notes = [],
  summary,
  previousPeriodData,
  channels = [],
  geography = [],
  devices = [],
  browsers = [],
  languages = [],
  landingPages = [],
  weekdays = [],
  ecommerceItems = [],
  events = [],
  granularity = 'day',
  onGranularityChange,
  loading = false,
  KPICard,
  formatNumber,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_results',
  // Props pour la comparaison
  compareEnabled = false,
  comparisonType = '',
  // De quoi recharger les achats e-commerce à un autre grain, sans rejouer tout le dashboard.
  token,
  siteId,
  period,
  customStartDate,
  customEndDate
}) => {
  const [showPerformanceChart, setShowPerformanceChart] = useState(true);
  const [selectedLineMetric, setSelectedLineMetric] = useState('conversionRate'); // RATIO par défaut
  const [selectedBarMetric, setSelectedBarMetric] = useState('conversions'); // VALEUR par défaut
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Normaliser les données de la période précédente/comparaison
  const prevSummary = useMemo(() => {
    if (!previousPeriodData) return null;
    return previousPeriodData.summary || previousPeriodData;
  }, [previousPeriodData]);

  // Label pour la période de comparaison
  const comparisonLabel = comparisonType === 'previous_year' ? 'année précédente' : 'période précédente';

  // Hook pour les métriques du composant Résultats (réactif aux changements)
  const resultsMetrics = useComponentMetrics('matomo_results', accountId);

  /**
   * L'intitulé d'une carte, pris dans la table commune aux trois solutions de mesure.
   *
   * Ils étaient écrits en dur — « Visites », « Pages/Visite ». La table dit « Sessions » et
   * « Pages/Session » : la modale de configuration et la carte annonçaient deux choses
   * différentes pour la même métrique.
   */
  const libelleResultats = (cle) => getMetricLabel(cle, 'matomo_results', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée pour Résultats
  const isResultsMetricActive = useCallback((metricKey) => {
    return resultsMetrics.metrics.includes(metricKey);
  }, [resultsMetrics.metrics]);

  // Filtrer les métriques disponibles selon celles activées dans "Configurer les métriques"
  const enabledMetrics = resultsMetrics?.metrics || [];
  const availableValueMetrics = useMemo(() =>
    MATOMO_VALUE_METRICS.filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );
  // La courbe peut afficher TOUTES les métriques (VALEURS + RATIOS)
  const availableLineMetrics = useMemo(() =>
    [...MATOMO_VALUE_METRICS, ...MATOMO_RATIO_METRICS].filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );

  /**
   * Dernière configuration de graphe appliquée, pour ne pas écraser un choix manuel à chaque
   * rendu — mais reprendre la main dès qu'un template est appliqué.
   *
   * Un simple booléen vivait ici : la configuration n'était posée qu'une fois par montage, si
   * bien que changer de template en cours de session laissait le graphe inchangé. La comparer
   * permet de distinguer « déjà fait » de « redemandé ».
   */
  const chartConfigAppliedRef = useRef(null);

  /**
   * Choisir la métrique des barres ou de la courbe, **et s'en souvenir**.
   *
   * Les deux `setState` vivaient dans chaque carte. Le réglage ne survivait donc pas au
   * rechargement : seul un modèle écrivait un `chartConfig`, jamais un clic.
   */
  const choisirMetriqueGraphe = useCallback((barres, courbe) => {
    setSelectedBarMetric(barres);
    setSelectedLineMetric(courbe);
    // La référence suit, sinon l'effet de configuration rejouerait le couple qu'on vient
    // d'écrire — sans dommage, mais pour rien.
    chartConfigAppliedRef.current = JSON.stringify({ barMetric: barres, lineMetric: courbe });
    saveChartConfig('matomo_results', { barMetric: barres, lineMetric: courbe }, accountId);
  }, [accountId]);

  /**
   * Le couple barres/courbe : d'abord ce que le modèle impose, ensuite ce que les métriques
   * actives autorisent — **dans cet ordre, et dans un seul effet**.
   *
   * ## Le défaut
   *
   * C'était deux effets séparés. Au montage, le premier posait le couple du modèle
   * (`transactions` en barres, `visites` en courbe) ; le second, qui vérifie qu'une métrique
   * sélectionnée est bien active, s'exécutait dans la **même passe** et lisait donc l'état
   * *d'avant* — les valeurs par défaut du composant, `conversions` et `conversionRate`.
   *
   * Or le modèle E-commerce n'active ni l'une ni l'autre. Le second effet les jugeait
   * indisponibles, les remplaçait par les premières venues — `visites` et `rebond` — et ses
   * écritures, posées après, l'emportaient. Le réglage « sautait » à chaque rechargement, quoi
   * qu'on fasse par ailleurs : persistance, repli sur le modèle, rien n'y pouvait, puisque le
   * couple était bien lu puis aussitôt écrasé.
   *
   * Un seul effet calcule donc la valeur voulue, la valide, et n'écrit qu'une fois.
   */
  useEffect(() => {
    const chartConfig = resultsMetrics?.chartConfig;
    const configKey = chartConfig ? JSON.stringify(chartConfig) : null;
    const configNouvelle = configKey && chartConfigAppliedRef.current !== configKey;

    let barres = configNouvelle && chartConfig.barMetric ? chartConfig.barMetric : selectedBarMetric;
    let courbe = configNouvelle && chartConfig.lineMetric ? chartConfig.lineMetric : selectedLineMetric;

    const cleDe = (valeur) => Object.keys(MATOMO_METRIC_KEY_MAP)
      .find((k) => MATOMO_METRIC_KEY_MAP[k] === valeur);

    // La courbe accepte valeurs et ratios ; à défaut, on préfère un ratio.
    const cleCourbe = cleDe(courbe);
    if (cleCourbe && !availableLineMetrics.includes(cleCourbe)) {
      const repli = availableLineMetrics.find((m) => MATOMO_RATIO_METRICS.includes(m))
        || availableLineMetrics[0];
      if (repli) courbe = MATOMO_METRIC_KEY_MAP[repli];
    }

    // Les barres préfèrent une valeur à un ratio.
    const cleBarres = cleDe(barres);
    if (cleBarres && !availableLineMetrics.includes(cleBarres)) {
      const repli = availableLineMetrics.find((m) => MATOMO_VALUE_METRICS.includes(m))
        || availableLineMetrics[0];
      if (repli) barres = MATOMO_METRIC_KEY_MAP[repli];
    }

    if (configNouvelle) chartConfigAppliedRef.current = configKey;
    if (barres !== selectedBarMetric) setSelectedBarMetric(barres);
    if (courbe !== selectedLineMetric) setSelectedLineMetric(courbe);
  }, [resultsMetrics?.chartConfig, availableLineMetrics, selectedLineMetric, selectedBarMetric]);

  // Formateur de temps (secondes -> mm:ss)
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Configuration des métriques pour la courbe
  const lineMetricConfig = {
    visits: { label: 'Sessions', color: '#82ca9d', unit: '', format: (val) => formatNumber(val) },
    conversionsValue: { label: 'Valeur Conv.', color: '#06b6d4', unit: '€', format: (val) => formatNumber(val, 2) },
    pageviews: { label: 'Pages vues', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    bounceRate: { label: 'Rebond', color: '#8b5cf6', unit: '%', format: (val) => formatNumber(val, 1) },
    avgTimeOnSite: { label: 'Durée moy.', color: '#f97316', unit: '', format: (val) => formatTime(val) },
    actionsPerVisit: { label: 'Pages/Session', color: '#ec4899', unit: '', format: (val) => formatNumber(val, 1) },
    conversionRate: { label: 'Taux de conv.', color: '#f59e0b', unit: '%', format: (val) => formatNumber(val, 2) },
    conversions: { label: 'Conversions', color: '#8884d8', unit: '', format: (val) => formatNumber(val) },
    transactions: { label: 'Transactions', color: '#10b981', unit: '', format: (val) => formatNumber(val) },
    totalRevenue: { label: 'Chiffre d\'affaires', color: '#06b6d4', unit: '€', format: (val) => formatNumber(val, 2) },
    transactionRate: { label: '% Conversion (transactions)', color: '#f97316', unit: '%', format: (val) => formatNumber(val, 2) },
    avgOrderValue: { label: 'Panier moyen', color: '#ec4899', unit: '€', format: (val) => formatNumber(val, 2) }
  };

  // Obtenir la valeur de la métrique sélectionnée pour chaque point
  const getLineMetricValue = (d) => {
    switch (selectedLineMetric) {
      case 'visits': return d.visits || 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'pageviews': return d.pageviews || 0;
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'actionsPerVisit': return d.actionsPerVisit || 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'conversions': return d.conversions || 0;
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      case 'avgOrderValue': return (d.transactions || 0) > 0 ? (d.totalRevenue || 0) / d.transactions : 0;
      default: return d.visits || 0;
    }
  };

  // Fonction pour calculer la valeur de la métrique des barres
  const getBarMetricValue = (d) => {
    switch (selectedBarMetric) {
      case 'visits': return d.visits || 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'pageviews': return d.pageviews || 0;
      case 'conversions': return d.conversions || 0;
      // RATIOs
      case 'bounceRate': return d.bounceRate || 0;
      case 'avgTimeOnSite': return d.avgTimeOnSite || 0;
      case 'actionsPerVisit': return d.actionsPerVisit || 0;
      case 'conversionRate': return d.visits > 0 ? ((d.conversions || 0) / d.visits * 100) : 0;
      case 'transactions': return d.transactions || 0;
      case 'totalRevenue': return d.totalRevenue || 0;
      case 'transactionRate': return d.visits > 0 ? ((d.transactions || 0) / d.visits * 100) : 0;
      case 'avgOrderValue': return (d.transactions || 0) > 0 ? (d.totalRevenue || 0) / d.transactions : 0;
      default: return d.conversions || 0;
    }
  };

  // Formater les labels de date selon la granularité
  const formatDateLabel = (dateStr, gran) => {
    if (!dateStr) return '';

    switch (gran) {
      case 'week':
        return `Sem. ${dateStr}`;
      case 'month':
        if (dateStr.includes('/')) return dateStr;
        if (dateStr.includes('-') && dateStr.length === 7) {
          const [year, month] = dateStr.split('-');
          return `${month}/${year}`;
        }
        return dateStr;
      case 'quarter':
      case 'year':
        return dateStr;
      default: // day
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const date = new Date(dateStr);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        }
        return dateStr;
    }
  };

  // Données du graphique avec métrique sélectionnée
  const { survolee, position, onSurvol, onSortie } = useNotesDeGraphique();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(d => ({
      ...d,
      lineMetricValue: getLineMetricValue(d),
      barMetricValue: getBarMetricValue(d)
    }));
  }, [data, selectedLineMetric, selectedBarMetric]);

  // Calculer les KPI à partir du summary ou des données
  const kpiData = useMemo(() => {
    // Utiliser le summary si disponible
    if (summary) {
      return {
        totalVisits: summary.totalVisits || 0,
        totalConversionsValue: summary.conversionsValue || 0,
        totalPageviews: summary.totalPageviews || 0,
        totalConversions: summary.conversions || 0,
        avgBounceRate: summary.bounceRate || 0,
        avgTimeOnSite: summary.avgTimeOnSite || 0,
        avgActionsPerVisit: summary.actionsPerVisit || 0,
        conversionRate: summary.conversionRate || 0,
        // Les quatre mesures e-commerce, sous les noms de GA4 et de Piano.
        totalTransactions: summary.transactions || 0,
        totalRevenue: summary.totalRevenue || 0,
        transactionRate: summary.transactionRate || 0,
        avgOrderValue: summary.avgOrderValue || 0,
        /*
         * ⚠️ **`uniqueVisitors` n'est pas ramené à zéro, et c'est voulu.**
         *
         * Le backend ne pose ce champ que lorsque Matomo a réellement dédoublonné les
         * visiteurs sur la période. Son absence est donc le signal qui fait basculer la carte
         * sur le cumul quotidien. Un `|| 0` effacerait ce signal et afficherait zéro visiteur
         * sur un site qui en a — exactement le défaut que cette mesure a connu côté serveur.
         */
        uniqueVisitors: summary.uniqueVisitors,
        uniqueVisitorsNote: summary.uniqueVisitorsNote,
        uniqueVisitorsDaily: summary.uniqueVisitorsDaily || 0
      };
    }

    // Sinon calculer depuis les données quotidiennes
    if (!data || data.length === 0) {
      return {
        totalVisits: 0,
        totalConversionsValue: 0,
        totalPageviews: 0,
        totalConversions: 0,
        avgBounceRate: 0,
        avgTimeOnSite: 0,
        avgActionsPerVisit: 0,
        conversionRate: 0
      };
    }

    const totals = data.reduce((acc, d) => ({
      visits: acc.visits + (d.visits || 0),
      conversionsValue: acc.conversionsValue + (d.conversionsValue || 0),
      pageviews: acc.pageviews + (d.pageviews || 0),
      conversions: acc.conversions + (d.conversions || 0),
      transactions: acc.transactions + (d.transactions || 0),
      totalRevenue: acc.totalRevenue + (d.totalRevenue || 0),
      bounceSum: acc.bounceSum + ((d.bounceRate || 0) * (d.visits || 0)),
      timeSum: acc.timeSum + ((d.avgTimeOnSite || 0) * (d.visits || 0)),
      actionsSum: acc.actionsSum + ((d.actionsPerVisit || 0) * (d.visits || 0))
    }), { visits: 0, conversionsValue: 0, pageviews: 0, conversions: 0, transactions: 0, totalRevenue: 0, bounceSum: 0, timeSum: 0, actionsSum: 0 });

    return {
      totalVisits: totals.visits,
      totalConversionsValue: totals.conversionsValue,
      totalPageviews: totals.pageviews,
      totalConversions: totals.conversions,
      avgBounceRate: totals.visits > 0 ? totals.bounceSum / totals.visits : 0,
      avgTimeOnSite: totals.visits > 0 ? totals.timeSum / totals.visits : 0,
      avgActionsPerVisit: totals.visits > 0 ? totals.actionsSum / totals.visits : 0,
      conversionRate: totals.visits > 0 ? (totals.conversions / totals.visits * 100) : 0,
      totalTransactions: totals.transactions,
      totalRevenue: totals.totalRevenue,
      transactionRate: totals.visits > 0 ? (totals.transactions / totals.visits * 100) : 0,
      avgOrderValue: totals.transactions > 0 ? totals.totalRevenue / totals.transactions : 0
    };
  }, [data, summary]);

  /**
   * Une carte de métrique, avec sa bascule barres / courbe.
   *
   * Les cartes historiques sont écrites à la main, chacune répétant la même quarantaine de
   * lignes. Les métriques ajoutées pour aligner Matomo sur GA4 et Piano passent par ce rendu :
   * quatre de plus en copie intégrale auraient fait cent soixante lignes dont la seule
   * différence tient en six valeurs.
   *
   * @param {string} cle      la métrique au sens de « Configurer les métriques »
   * @param {string} champ    le champ des données quotidiennes, et celui du summary précédent
   * @param {Function} serie  ce qu'on trace pour un jour donné
   */
  /*
   * `precedent` prime sur le sommaire : le nom du champ d'une ligne ne correspond pas toujours
   * à celui du total — les visiteurs cumulés s'appellent `uniqueVisitorsDaily` —, et la carte
   * restait alors sans bandeau de comparaison, ce qui se lit comme une absence de donnée.
   */
  const carteMetrique = ({ cle, champ, titre, valeur, couleur, unite, decimales = 0, serie, nonMesure = false, mention, precedent }) => {
    if (!isResultsMetricActive(cle)) return null;

    const basculer = () => {
      if (selectedBarMetric === champ) {
        // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
        choisirMetriqueGraphe(selectedLineMetric || champ, champ);
        return;
      }
      choisirMetriqueGraphe(champ, selectedBarMetric || selectedLineMetric);
    };

    const choisie = selectedBarMetric === champ || selectedLineMetric === champ;

    return (
      <div
        onClick={nonMesure ? undefined : basculer}
        style={{
          cursor: nonMesure ? 'default' : 'pointer',
          borderRadius: '8px',
          outline: choisie && !nonMesure ? `3px solid ${couleur}` : 'none',
          outlineOffset: '2px',
          transition: 'outline 0.2s'
        }}
        title={mention
          || (selectedBarMetric === champ ? 'Affiché en barres' : selectedLineMetric === champ ? 'Affiché en courbe' : 'Cliquez pour afficher en barres')}
      >
        {/*
          Une mesure que la régie ne sert pas affiche un tiret, jamais un zéro : « 0 visiteur »
          se lit comme un constat, « — » comme une absence de mesure.
        */}
        <KPICard
          title={titre}
          value={nonMesure ? 0 : valeur}
          unit={nonMesure ? undefined : unite}
          color={couleur}
          currentPeriodData={nonMesure ? [] : (data?.map(serie || (d => d[champ] || 0)) || [])}
          previousValue={nonMesure ? undefined : (precedent ?? prevSummary?.[champ])}
          comparisonLabel={comparisonLabel}
          formatter={(val) => (nonMesure ? '—' : formatNumber(val, decimales))}
          className={getMetricObjectiveClassName(cle, valeur, 'matomo_results', accountId)}
        />
      </div>
    );
  };

  // Configuration métrique courbe actuelle
  const currentLineConfig = lineMetricConfig[selectedLineMetric];

  // Configuration des barres (pour TOUTES les métriques : VALEURS + RATIOS)
  const barMetricConfig = useMemo(() => {
    const configs = {
      visits: { name: 'Sessions', fill: '#82ca9d' },
      pageviews: { name: 'Pages vues', fill: '#10b981' },
      conversionsValue: { name: 'Valeur Conv.', fill: '#06b6d4' },
      conversions: { name: 'Conversions', fill: '#8884d8' },
      // RATIOs
      conversionRate: { name: 'Taux de conv.', fill: '#f59e0b' },
      bounceRate: { name: 'Rebond', fill: '#8b5cf6' },
      avgTimeOnSite: { name: 'Durée moy.', fill: '#f97316' },
      actionsPerVisit: { name: 'Pages/Session', fill: '#14b8a6' },
      transactions: { name: 'Transactions', fill: '#10b981' },
      totalRevenue: { name: 'Chiffre d\'affaires', fill: '#06b6d4' },
      transactionRate: { name: '% Conversion (transactions)', fill: '#f97316' },
      avgOrderValue: { name: 'Panier moyen', fill: '#ec4899' }
    };
    const config = configs[selectedBarMetric] || configs.conversions;
    return { dataKey: 'barMetricValue', ...config };
  }, [selectedBarMetric]);

  // Si en chargement ou pas de données, retourner null (l'écran de chargement est géré par MatomoDashboardView)
  if (loading || !summary) {
    return null;
  }

  // Style pour les conteneurs crème individuels
  const sectionStyle = {
    background: '#FAF9F5',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  return (
    <>
      {/* Section Résultats */}
      <div style={sectionStyle}>
        <div className="section-header">
          <h4><TrendingUp size={20} />&nbsp;Résultats</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Boutons de granularité */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { value: 'day', label: 'Jour' },
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
              { value: 'quarter', label: 'Trimestre' },
              { value: 'year', label: 'Année' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onGranularityChange?.(value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: granularity === value ? '#1a1a1a' : 'white',
                  color: granularity === value ? 'white' : '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title={`Afficher par ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Résultats Matomo"
            />
          ) : (
            <button
              onClick={() => setShowPerformanceChart(!showPerformanceChart)}
              className="section-toggle-btn"
              title={showPerformanceChart ? "Masquer la section" : "Afficher la section"}
            >
              {showPerformanceChart ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {showPerformanceChart && (
        <>
          {/* Message si pas de données */}
          {(!data || data.length === 0) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '12px',
              color: '#6b7280',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <AlertCircle size={16} style={{ marginRight: '8px', opacity: 0.6 }} />
              Aucune donnée disponible pour cette période
            </div>
          )}

          {/* Graphique */}
          <div className="full-width-chart" style={{
            width: '100%',
            minWidth: '300px',
            height: '400px',
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  type="category"
                  allowDuplicatedCategory={false}
                  tickFormatter={(value) => formatDateLabel(value, granularity)}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke={currentLineConfig.color}
                  tick={{ fontSize: 12 }}
                  label={{ value: currentLineConfig.label, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  tick={{ fontSize: 12 }}
                  label={{ value: barMetricConfig.name, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => {
                    if (name === currentLineConfig.label) {
                      return [currentLineConfig.format(value) + currentLineConfig.unit, name];
                    }
                    /*
                      La série en barres n'avait aucun formatage propre : elle retombait sur
                      `formatNumber(value)`, c'est-à-dire un entier. Toute métrique de ratio
                      choisie pour l'histogramme s'affichait donc « 0 » dans l'infobulle — un
                      CPC de 0,0164 € ou un coût par interaction de 0,016 €. La carte, elle,
                      montrait 0,02 €, et les deux se contredisaient à l'écran.

                      Le formatage est repris de `lineMetricConfig`, la table qui porte déjà
                      l'unité et le nombre de décimales de chaque métrique. Une seconde table
                      propre aux barres aurait fini par diverger de la première.
                    */
                    const configBarre = lineMetricConfig[selectedBarMetric];
                    if (configBarre && name === barMetricConfig.name) {
                      return [configBarre.format(value) + configBarre.unit, name];
                    }
                    return [formatNumber(value), name];
                  }}
                />
                <Legend />

                {/* Repères des notes. Placés après la légende pour passer au-dessus des
                    séries, comme sur les dashboards de régies. */}
                {marqueursDeNotes({
                  notes,
                  donneesDuGraphique: chartData,
                  granularite: granularity,
                  onSurvol,
                  onSortie
                })}

                {/* Barres */}
                <Bar
                  yAxisId="right"
                  dataKey={barMetricConfig.dataKey}
                  fill={barMetricConfig.fill}
                  fillOpacity={0.8}
                  name={barMetricConfig.name}
                  radius={[4, 4, 0, 0]}
                />

                {/* Courbe */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="lineMetricValue"
                  stroke={currentLineConfig.color}
                  strokeWidth={3}
                  name={currentLineConfig.label}
                  dot={{ fill: currentLineConfig.color, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: currentLineConfig.color }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>

          {/* L'infobulle est en position fixe : hors du graphique, elle n'est pas rognée
              par ses marges. */}
          <InfobulleDeNotes survolee={survolee} position={position} />
          </div>

          {/* KPI Cards - Grille 4 colonnes comme Google Ads */}
          <div className="chart-insights" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginTop: '16px'
          }}>
            {/* Visites */}
            {/*
              Les cartes **dans l'ordre du modèle**.

              Elles étaient écrites à la suite, chacune sous son propre `isResultsMetricActive` : le
              modèle réglait donc lesquelles afficher, jamais dans quel ordre. C'est la forme que
              `GA4PerformanceChart` emploie déjà.
            */}
            {(resultsMetrics.order?.length ? resultsMetrics.order : resultsMetrics.metrics || []).map((metricKey) => {
              if (!isResultsMetricActive(metricKey)) return null;

              switch (metricKey) {
                case 'visites':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'visits') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'visits', 'visits');
                          return;
                        }
                        choisirMetriqueGraphe('visits', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'visits'
                    ? '3px solid #82ca9d'
                    : selectedLineMetric === 'visits'
                      ? '3px solid #82ca9d'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'visits' ? 'Affiché en barres' : selectedLineMetric === 'visits' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('visites')}
                  value={kpiData.totalVisits}
                  color="#82ca9d"
                  currentPeriodData={data?.map(d => d.visits) || []}
                  previousValue={prevSummary?.totalVisits}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'taux_conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversionRate') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversionRate', 'conversionRate');
                          return;
                        }
                        choisirMetriqueGraphe('conversionRate', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversionRate'
                    ? '3px solid #f59e0b'
                    : selectedLineMetric === 'conversionRate'
                      ? '3px solid #f59e0b'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversionRate' ? 'Affiché en barres' : selectedLineMetric === 'conversionRate' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('taux_conv')}
                  value={kpiData.conversionRate}
                  unit="%"
                  color="#f59e0b"
                  currentPeriodData={data?.map(d => d.visits > 0 ? (d.conversions || 0) / d.visits * 100 : 0) || []}
                  previousValue={prevSummary?.conversionRate}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 2)}
                  className={getMetricObjectiveClassName('taux_conv', kpiData.conversionRate, 'matomo_results', accountId)}
                />
              </div>
                  );
                case 'conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversions') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversions', 'conversions');
                          return;
                        }
                        choisirMetriqueGraphe('conversions', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversions'
                    ? '3px solid #8884d8'
                    : selectedLineMetric === 'conversions'
                      ? '3px solid #8884d8'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversions' ? 'Affiché en barres' : selectedLineMetric === 'conversions' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('conv')}
                  value={kpiData.totalConversions}
                  color="#8884d8"
                  currentPeriodData={data?.map(d => d.conversions || 0) || []}
                  previousValue={prevSummary?.conversions}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'pages_vues':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'pageviews') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'pageviews', 'pageviews');
                          return;
                        }
                        choisirMetriqueGraphe('pageviews', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'pageviews'
                    ? '3px solid #10b981'
                    : selectedLineMetric === 'pageviews'
                      ? '3px solid #10b981'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'pageviews' ? 'Affiché en barres' : selectedLineMetric === 'pageviews' ? 'Affiché en courbe' : 'Cliquez pour afficher'}
              >
                <KPICard
                  title={libelleResultats('pages_vues')}
                  value={kpiData.totalPageviews}
                  color="#10b981"
                  currentPeriodData={data?.map(d => d.pageviews) || []}
                  previousValue={prevSummary?.totalPageviews}
                  comparisonLabel={comparisonLabel}
                />
              </div>
                  );
                case 'rebond':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'bounceRate') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'bounceRate', 'bounceRate');
                          return;
                        }
                        choisirMetriqueGraphe('bounceRate', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'bounceRate'
                    ? '3px solid #8b5cf6'
                    : selectedLineMetric === 'bounceRate'
                      ? '3px solid #8b5cf6'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'bounceRate' ? 'Affiché en barres' : selectedLineMetric === 'bounceRate' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('rebond')}
                  value={kpiData.avgBounceRate}
                  unit="%"
                  color="#8b5cf6"
                  currentPeriodData={data?.map(d => d.bounceRate || 0) || []}
                  previousValue={prevSummary?.bounceRate}
                  comparisonLabel={comparisonLabel}
                  invertVariation={true}
                  formatter={(val) => formatNumber(val, 1)}
                  className={getMetricObjectiveClassName('rebond', kpiData.avgBounceRate, 'matomo_results', accountId)}
                />
              </div>
                  );
                case 'duree_moy':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'avgTimeOnSite') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'avgTimeOnSite', 'avgTimeOnSite');
                          return;
                        }
                        choisirMetriqueGraphe('avgTimeOnSite', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'avgTimeOnSite'
                    ? '3px solid #f97316'
                    : selectedLineMetric === 'avgTimeOnSite'
                      ? '3px solid #f97316'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'avgTimeOnSite' ? 'Affiché en barres' : selectedLineMetric === 'avgTimeOnSite' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('duree_moy')}
                  value={kpiData.avgTimeOnSite}
                  color="#f97316"
                  currentPeriodData={data?.map(d => d.avgTimeOnSite || 0) || []}
                  previousValue={prevSummary?.avgTimeOnSite}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatTime(val)}
                  className={getMetricObjectiveClassName('duree_moy', kpiData.avgTimeOnSite, 'matomo_results', accountId)}
                />
              </div>
                  );
                case 'valeur_conv':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'conversionsValue') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'conversionsValue', 'conversionsValue');
                          return;
                        }
                        choisirMetriqueGraphe('conversionsValue', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'conversionsValue'
                    ? '3px solid #06b6d4'
                    : selectedLineMetric === 'conversionsValue'
                      ? '3px solid #06b6d4'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'conversionsValue' ? 'Affiché en barres' : selectedLineMetric === 'conversionsValue' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('valeur_conv')}
                  value={kpiData.totalConversionsValue}
                  unit="€"
                  color="#06b6d4"
                  currentPeriodData={data?.map(d => d.conversionsValue || 0) || []}
                  previousValue={prevSummary?.conversionsValue}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 2)}
                />
              </div>
                  );
                case 'pages_visite':
                  return (
              <div
                key={metricKey}
                onClick={() => {
                  if (selectedBarMetric === 'actionsPerVisit') {
                          // Déjà en barres : la métrique passe en courbe, l'autre prend sa place.
                          choisirMetriqueGraphe(selectedLineMetric || 'actionsPerVisit', 'actionsPerVisit');
                          return;
                        }
                        choisirMetriqueGraphe('actionsPerVisit', selectedBarMetric || selectedLineMetric);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  outline: selectedBarMetric === 'actionsPerVisit'
                    ? '3px solid #ec4899'
                    : selectedLineMetric === 'actionsPerVisit'
                      ? '3px solid #ec4899'
                      : 'none',
                  outlineOffset: '2px',
                  transition: 'outline 0.2s'
                }}
                title={selectedBarMetric === 'actionsPerVisit' ? 'Affiché en barres' : selectedLineMetric === 'actionsPerVisit' ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
              >
                <KPICard
                  title={libelleResultats('pages_visite')}
                  value={kpiData.avgActionsPerVisit}
                  color="#ec4899"
                  currentPeriodData={data?.map(d => d.actionsPerVisit || 0) || []}
                  previousValue={prevSummary?.actionsPerVisit}
                  comparisonLabel={comparisonLabel}
                  formatter={(val) => formatNumber(val, 1)}
                  className={getMetricObjectiveClassName('pages_visite', kpiData.avgActionsPerVisit, 'matomo_results', accountId)}
                />
              </div>
                  );
                case 'visiteurs':
                  return (
                    <React.Fragment key={metricKey}>
                      {carteMetrique({
                        cle: 'visiteurs',
                        champ: 'visitors',
                        /*
                         * **Deux mesures possibles sous le même titre, et la mention dit
                         * laquelle est affichée.**
                         *
                         * Matomo ne dédoublonne pas les visiteurs sur une plage de dates, et
                         * l'appel partait en plage même pour « ce mois-ci » (du 1er à hier). La
                         * carte ne pouvait donc afficher que la somme des uniques de chaque
                         * jour, où un visiteur revenu cinq fois compte cinq fois.
                         *
                         * Le serveur demande désormais la mesure dans la période native qui
                         * couvre la même unité de calendrier — « ce mois-ci » *est* un mois
                         * civil — et pose `uniqueVisitors` quand elle est réellement
                         * dédoublonnée. Deux cas restent sans réponse : une fenêtre glissante
                         * (« 30 derniers jours »), qui n'est l'unité de calendrier de rien, et
                         * une instance qui coupe le calcul mensuel. Le cumul reste alors le
                         * repli, et la mention le dit.
                         */
                        titre: libelleResultats('visiteurs'),
                        valeur: kpiData.uniqueVisitors ?? kpiData.uniqueVisitorsDaily,
                        couleur: '#8b5cf6',
                        serie: (d) => d.visitors || 0,
                        /*
                         * Le précédent suit la nature de la valeur. Le serveur ne pose
                         * `uniqueVisitors` sur la période précédente que si les deux périodes
                         * l'ont : sans ce garde-fou, le bandeau opposerait un décompte
                         * dédoublonné à un cumul et inventerait une variation.
                         */
                        precedent: kpiData.uniqueVisitors !== undefined
                          ? prevSummary?.uniqueVisitors
                          : prevSummary?.uniqueVisitorsDaily,
                        mention: kpiData.uniqueVisitors !== undefined
                          ? (kpiData.uniqueVisitorsNote
                            || 'Visiteurs dédoublonnés par Matomo sur la période.')
                          : 'Somme des visiteurs uniques de chaque jour : un visiteur revenu '
                            + 'plusieurs jours y compte plusieurs fois. '
                            + (kpiData.uniqueVisitorsNote
                              || "Matomo ne dédoublonne pas sur cette période.")
                      })}
                    </React.Fragment>
                  );
                case 'transactions':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'transactions',
                  champ: 'transactions',
                  titre: 'Transactions',
                  valeur: kpiData.totalTransactions,
                  couleur: '#10b981'
                })}
                    </React.Fragment>
                  );
                case 'chiffre_affaires':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'chiffre_affaires',
                  champ: 'totalRevenue',
                  titre: 'Chiffre d\'affaires',
                  valeur: kpiData.totalRevenue,
                  couleur: '#06b6d4',
                  unite: '€',
                  decimales: 2
                })}
                    </React.Fragment>
                  );
                case 'taux_transaction':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'taux_transaction',
                  champ: 'transactionRate',
                  titre: '% Conversion (transactions)',
                  valeur: kpiData.transactionRate,
                  couleur: '#f97316',
                  unite: '%',
                  decimales: 2
                })}
                    </React.Fragment>
                  );
                case 'panier_moyen':
                  return (
                    <React.Fragment key={metricKey}>
              {carteMetrique({
                  cle: 'panier_moyen',
                  champ: 'avgOrderValue',
                  titre: 'Panier moyen',
                  valeur: kpiData.avgOrderValue,
                  couleur: '#ec4899',
                  unite: '€',
                  decimales: 2,
                  // Le panier moyen ne se lit pas dans les données du jour : il se calcule.
                  serie: (d) => ((d.transactions || 0) > 0 ? (d.totalRevenue || 0) / d.transactions : 0)
                })}
                    </React.Fragment>
                  );
                default:
                  return null;
              }
            })}

          </div>
        </>
      )}
      </div>

      {/* Composant Canaux */}
      <div style={sectionStyle}>
        <MatomoChannelsChart
          channels={channels}
          previousChannels={previousPeriodData?.channels}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_channels"
        />
      </div>

      {/* Composant Géographie */}
      <div style={sectionStyle}>
        <MatomoGeographyChart
          geography={geography}
          previousGeography={previousPeriodData?.geography}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_geography"
        />
      </div>

      {/* Composant Appareils */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={devices}
          lignesPrecedentes={previousPeriodData?.devices}
          cleDimension="device"
          cleLibelle="deviceLabel"
          titre="Appareils"
          intituleColonne="Appareil"
          nomAuSingulier="appareil"
          icone={Monitor}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="matomo_devices"
          moduleId="matomo_devices"
        />
      </div>

      {/* Composant Navigateurs */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={browsers}
          lignesPrecedentes={previousPeriodData?.browsers}
          cleDimension="browser"
          cleLibelle="browserLabel"
          titre="Navigateurs"
          intituleColonne="Navigateur"
          nomAuSingulier="navigateur"
          icone={Globe}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="matomo_browsers"
          moduleId="matomo_browsers"
        />
      </div>

      {/* Composant Langues */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={languages}
          lignesPrecedentes={previousPeriodData?.languages}
          cleDimension="language"
          cleLibelle="languageLabel"
          titre="Langues"
          intituleColonne="Langue"
          nomAuSingulier="langue"
          /* Un seul camembert : Matomo n'archive aucun objectif par langue. */
          graphe="camembert"
          icone={Languages}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="matomo_languages"
          moduleId="matomo_languages"
        />
      </div>

      {/* Composant Jours de la semaine */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={weekdays}
          lignesPrecedentes={previousPeriodData?.weekdays}
          cleDimension="weekday"
          cleLibelle="weekdayLabel"
          titre="Jours de la semaine"
          intituleColonne="Jour"
          nomAuSingulier="jour"
          /* Lundi à dimanche, et le graphe à barres de Google Analytics. */
          triInitial={{ key: null, direction: 'desc' }}
          graphe="barres"
          icone={Calendar}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="matomo_weekdays"
          moduleId="matomo_weekdays"
        />
      </div>

      {/* Composant Pages d'atterrissage */}
      <div style={sectionStyle}>
        <RepartitionChart
          lignes={landingPages}
          lignesPrecedentes={previousPeriodData?.landingPages}
          cleDimension="landingPage"
          cleLibelle="landingPageLabel"
          titre="Pages d'atterrissage"
          intituleColonne="Page"
          nomAuSingulier="page"
          /* Cinquante URL ne se lisent pas en parts : le tableau seul. */
          graphe="aucun"
          icone={FileText}
          visiteursDuSite={kpiData.uniqueVisitorsDaily}
          compareEnabled={compareEnabled}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          componentId="matomo_landing_pages"
          moduleId="matomo_landing_pages"
        />
      </div>

      {/*
        Les événements, juste après les pages d'atterrissage.

        Ils vivaient dans la vue, après l'entonnoir : la séquence des répartitions était donc
        coupée en deux par un composant d'une autre nature.
      */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('matomo_events')) && (
        <div style={sectionStyle}>
          <MatomoEventsTable
            events={events}
            visitesDuSite={kpiData.totalVisits}
            visiteursDuSite={kpiData.uniqueVisitorsDaily}
            formatNumber={formatNumber}
            accountId={accountId}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="matomo_events"
          />
        </div>
      )}

      {/* Composant Achats d'e-commerce */}
      <div style={sectionStyle}>
        <MatomoEcommerceProducts
          items={ecommerceItems}
          commandesDuSite={kpiData.totalTransactions}
          chiffreAffairesDuSite={kpiData.totalRevenue}
          token={token}
          siteId={siteId}
          period={period}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          formatNumber={formatNumber}
          loading={loading}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_ecommerce_products"
        />
      </div>

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="matomo_results"
        accountId={accountId}
        componentTitle="Résultats"
      />
    </>
  );
};

export default MatomoPerformanceChart;
